import { LOCALES } from '@aysar/contracts';

import { assertCatalogParity, type LocaleCatalogs } from '../catalog-parity/catalog-parity';
import { i18n } from '../i18n.config';

/**
 * The namespaces registered so far, in registration order.
 *
 * Kept because the parity suite enumerates it: a catalog that is never
 * registered is a catalog nobody checks, and "we forgot to import the module's
 * i18n barrel" is otherwise invisible until a screen renders raw keys.
 */
const namespaces: string[] = [];

/**
 * Registers one namespace's ar + en trees (W2 — modules reach i18n only through
 * their `i18n/index.ts`, which the `app.config.tsx` bridge imports).
 *
 * Parity is asserted HERE, not only in a test: a namespace whose locales
 * disagree fails at import time, which is the moment a developer can still see
 * why. Waiting for the spec means the broken catalog can reach a branch, and
 * waiting for render means an Arabic-speaking operator finds it.
 *
 * Merging is deep and overwriting, so registering twice — which a barrel
 * imported from two places will do — extends the namespace instead of
 * truncating it.
 */
export function registerCatalog(namespace: string, catalogs: LocaleCatalogs): void {
  assertCatalogParity(namespace, catalogs);

  for (const locale of LOCALES) {
    i18n.addResourceBundle(locale, namespace, catalogs[locale], true, true);
  }

  if (!namespaces.includes(namespace)) {
    namespaces.push(namespace);
  }
}

/** Every namespace registered so far — the parity suite's input. */
export function registeredNamespaces(): readonly string[] {
  return [...namespaces];
}

/** Test-only: clears the registry so one spec's catalogs cannot leak into another's. */
export function resetCatalogs(): void {
  for (const namespace of namespaces) {
    for (const locale of LOCALES) {
      i18n.removeResourceBundle(locale, namespace);
    }
  }
  namespaces.length = 0;
}
